import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import {
  deleteTaxRule, listTaxRules, updateInvoiceIdentity, upsertTaxRule,
  type InvoiceIdentity, type TaxRule,
} from '@/api/billingAdmin'
import { COUNTRIES } from '@/data/countries'
import { Field, InfoBox, Td, Th, errText, tableStyle } from '../adminUi'

/**
 * Seller identity for invoices, and the tax rate applied per country.
 *
 * A country with no rule is charged no tax — which is the correct default
 * outside the seller's own jurisdiction, not an oversight.
 */
export default function TaxPanel() {
  const [rules, setRules] = useState<TaxRule[]>([])
  const [identity, setIdentity] = useState<InvoiceIdentity | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [country, setCountry] = useState('')
  const [taxName, setTaxName] = useState('GST')
  const [rate, setRate] = useState('')

  const load = useCallback(async () => {
    try {
      const data = await listTaxRules()
      setRules(data.rules)
      setIdentity(data.identity)
    } catch (e) {
      toast.error(errText(e, 'Could not load tax settings'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const saveIdentity = async () => {
    if (!identity) return
    setSaving(true)
    try {
      setIdentity(await updateInvoiceIdentity(identity))
      toast.success('Invoice identity saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the invoice identity'))
    } finally {
      setSaving(false)
    }
  }

  const addRule = async () => {
    const value = parseInt(rate, 10)
    if (!Number.isFinite(value)) { toast.error('Enter a rate'); return }
    try {
      const data = await upsertTaxRule({
        country: country || null, tax_name: taxName.trim() || 'TAX',
        rate_percent: value, applies_to: 'all', is_active: true,
      })
      setRules(data.rules)
      setRate('')
      toast.success('Tax rule saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the tax rule'))
    }
  }

  if (loading || !identity) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ maxWidth: 620 }}>
        <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.75rem' }}>Invoice identity</h3>
        <InfoBox>
          Printed at the top of every invoice, as the seller. Leaving these blank
          produces an invoice with no seller details — legal in nowhere.
        </InfoBox>
        <div style={{ display: 'grid', gap: '0.875rem', marginTop: '0.875rem' }}>
          <Field label="Legal name">
            <input value={identity.legal_name}
                   onChange={(e) => setIdentity({ ...identity, legal_name: e.target.value })}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="Registered address">
            <textarea value={identity.address} rows={3}
                      onChange={(e) => setIdentity({ ...identity, address: e.target.value })}
                      style={{ width: '100%' }} />
          </Field>
          <div style={{ display: 'grid', gap: '0.875rem', gridTemplateColumns: '1fr 1fr' }}>
            <Field label="Tax ID (GSTIN)">
              <input value={identity.tax_id}
                     onChange={(e) => setIdentity({ ...identity, tax_id: e.target.value.toUpperCase() })}
                     style={{ width: '100%', fontFamily: 'monospace' }} />
            </Field>
            <Field label="Seller state"
                   hint="Needed to decide CGST/SGST vs IGST once a split rule is configured.">
              <input value={identity.seller_state}
                     onChange={(e) => setIdentity({ ...identity, seller_state: e.target.value })}
                     style={{ width: '100%' }} />
            </Field>
          </div>
        </div>
        <button className="btn btn-primary" style={{ marginTop: '0.875rem' }}
                onClick={saveIdentity} disabled={saving}>
          {saving ? <Loader2 size={14} className="spin" /> : 'Save identity'}
        </button>
      </div>

      <div>
        <h3 style={{ fontSize: '1.0625rem', marginBottom: '0.75rem' }}>Tax rules</h3>
        <InfoBox>
          Tax is applied to the amount <em>after</em> discounts and coupons. A
          country with no rule is charged <strong>no tax</strong>. Add a rule with
          no country to tax every other country at one rate.
        </InfoBox>
        <table style={tableStyle}>
          <thead>
            <tr><Th>Country</Th><Th>Name</Th><Th right>Rate %</Th><Th right>Active</Th><Th /></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id}>
                <Td>
                  {r.country
                    ? (COUNTRIES.find((c) => c.code === r.country)?.name || r.country)
                    : <span style={{ color: 'var(--color-text-secondary)' }}>Rest of world</span>}
                </Td>
                <Td>{r.tax_name}</Td>
                <Td right>{r.rate_percent}</Td>
                <Td right>{r.is_active ? 'Yes' : 'No'}</Td>
                <Td right>
                  <button className="btn btn-secondary" style={{ padding: '0.1rem 0.35rem' }}
                          onClick={async () => {
                            try { setRules((await deleteTaxRule(r.id)).rules) }
                            catch (e) { toast.error(errText(e, 'Could not delete the rule')) }
                          }}>
                    <Trash2 size={12} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1.8fr 1fr 1fr auto', marginTop: '0.625rem', maxWidth: 620 }}>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">Rest of world</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input placeholder="Tax name" value={taxName} onChange={(e) => setTaxName(e.target.value)} />
          <input type="number" min={0} max={100} placeholder="Rate %" value={rate}
                 onChange={(e) => setRate(e.target.value)} />
          <button className="btn btn-secondary" onClick={addRule}><Plus size={14} /></button>
        </div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.375rem' }}>
          India is seeded at GST 18% as a single IGST line. A CGST/SGST split needs
          the seller state above and a component rule — until then every Indian
          invoice prints one IGST line.
        </div>
      </div>
    </div>
  )
}
